import React, { createContext, useContext, useState, ReactNode } from 'react';

interface SidebarContextType {
  expandedGroups: Record<string, boolean>;
  toggleGroup: (groupTitle: string) => void;
  expandGroup: (groupTitle: string) => void;
  userToggledGroups: Record<string, boolean>;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );
  const [userToggledGroups, setUserToggledGroups] = useState<
    Record<string, boolean>
  >({});

  const toggleGroup = (groupTitle: string) => {
    // Mark this group as manually toggled by user
    setUserToggledGroups((prev) => ({
      ...prev,
      [groupTitle]: true,
    }));

    setExpandedGroups((prev) => ({
      ...prev,
      [groupTitle]: !prev[groupTitle],
    }));
  };

  const expandGroup = (groupTitle: string) => {
    // Only expand if user hasn't manually toggled this group
    if (userToggledGroups[groupTitle]) return;

    setExpandedGroups((prev) => {
      if (prev[groupTitle]) return prev;
      return {
        ...prev,
        [groupTitle]: true,
      };
    });
  };

  return (
    <SidebarContext.Provider
      value={{ expandedGroups, toggleGroup, expandGroup, userToggledGroups }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
